import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Target, Heart, Save } from 'lucide-react';
import axios, { API } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import notify from '../utils/notify';

// Keep in sync with backend routes/organization.py::MAX_COMMITMENTS.
const MAX_COMMITMENTS = 5;

const THEME = {
  primary: '#5DBFAD',
  softBg: 'rgba(180, 225, 216, 0.1)',
  text: '#1F2937',
  textSecondary: '#6B7280',
  textMuted: '#9CA3AF',
  border: '#E5E7EB',
  surface: '#F9FAFB',
  danger: '#DC2626',
  white: '#FFFFFF'
};

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: `1px solid ${THEME.border}`, fontSize: 14, outline: 'none',
  boxSizing: 'border-box', background: THEME.white
};
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: THEME.textSecondary, marginBottom: 6 };
const sectionTitle = { fontSize: 13, fontWeight: 600, color: THEME.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 };

/**
 * HR-only edit screen for the /organization page's targets and
 * commitments. Route-level access is protected by a Navigate redirect
 * on non-HR users so a direct URL visit lands them back on /organization.
 * Backend routes it calls are separately gated by require_hr_admin, so
 * even if this guard is bypassed the mutations would fail 403.
 */
export default function EditOrgPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isHRAdmin = user?.role === 'HR_ADMIN';

  const [metricDefs, setMetricDefs] = useState([]);
  const [targets, setTargets] = useState([]);      // [{metric_id, target, baseline, baseline_method, _dirty, _original}]
  const [commitments, setCommitments] = useState([]);
  const [savingTargetId, setSavingTargetId] = useState(null);
  const [savingCommitments, setSavingCommitments] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isHRAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const [defsRes, tRes, oRes] = await Promise.all([
          axios.get(`${API}/impact/metric-definitions`),
          axios.get(`${API}/org/targets`),
          axios.get(`${API}/organizations/current`),
        ]);
        if (cancelled) return;
        setMetricDefs(Array.isArray(defsRes.data) ? defsRes.data : []);
        const existing = (Array.isArray(tRes.data) ? tRes.data : []).map(t => ({
          metric_id: t.metric_id,
          target: String(t.target ?? ''),
          baseline: String(t.baseline ?? '0'),
          baseline_method: t.baseline_method || '',
          _persisted: true,
        }));
        setTargets(existing);
        setCommitments(Array.isArray(oRes.data?.commitments) ? oRes.data.commitments.map(c => ({ ...c })) : []);
      } catch (err) {
        notify.error('Failed to load current settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isHRAdmin]);

  if (!isHRAdmin) {
    // Non-HR users cannot reach or see this page.
    return <Navigate to="/organization" replace />;
  }

  // ── Target handlers ──────────────────────────────────────────────

  const availableMetrics = () => {
    const used = new Set(targets.map(t => t.metric_id));
    return metricDefs.filter(m => !used.has(m.metric_id));
  };

  const addTargetRow = () => {
    const first = availableMetrics()[0];
    if (!first) {
      notify.error('All available metrics already have a target.');
      return;
    }
    setTargets(prev => [...prev, { metric_id: first.metric_id, target: '', baseline: '0', baseline_method: '', _persisted: false }]);
  };

  const updateTargetField = (idx, field, value) => {
    setTargets(prev => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  };

  const saveTarget = async (idx) => {
    const t = targets[idx];
    const targetNum = parseFloat(t.target);
    const baselineNum = parseFloat(t.baseline || '0');
    if (!t.metric_id) { notify.error('Pick a metric first'); return; }
    if (!Number.isFinite(targetNum) || targetNum <= 0) { notify.error('Target must be a positive number'); return; }
    if (!Number.isFinite(baselineNum) || baselineNum < 0) { notify.error('Baseline must be zero or positive'); return; }
    if (baselineNum >= targetNum) { notify.error('Baseline must be less than the target'); return; }

    setSavingTargetId(t.metric_id);
    try {
      await axios.put(`${API}/org/targets/${encodeURIComponent(t.metric_id)}`, {
        target: targetNum,
        baseline: baselineNum,
        baseline_method: t.baseline_method || null,
      });
      setTargets(prev => prev.map((row, i) => (i === idx ? { ...row, _persisted: true } : row)));
      notify.success('Target saved');
    } catch (err) {
      notify.error(err?.response?.data?.detail || 'Failed to save target');
    } finally {
      setSavingTargetId(null);
    }
  };

  const removeTargetRow = async (idx) => {
    const t = targets[idx];
    if (t._persisted) {
      try {
        await axios.delete(`${API}/org/targets/${encodeURIComponent(t.metric_id)}`);
      } catch (err) {
        notify.error(err?.response?.data?.detail || 'Failed to delete target');
        return;
      }
    }
    setTargets(prev => prev.filter((_, i) => i !== idx));
    notify.success('Target removed');
  };

  // ── Commitment handlers ──────────────────────────────────────────

  const addCommitment = () => {
    if (commitments.length >= MAX_COMMITMENTS) {
      notify.error(`Maximum ${MAX_COMMITMENTS} commitments`);
      return;
    }
    setCommitments(prev => [...prev, { title: '', description: '' }]);
  };

  const updateCommitmentField = (idx, field, value) => {
    setCommitments(prev => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  const removeCommitment = (idx) => {
    setCommitments(prev => prev.filter((_, i) => i !== idx));
  };

  const saveCommitments = async () => {
    // Validate each row locally before hitting the API.
    for (let i = 0; i < commitments.length; i++) {
      const c = commitments[i];
      if (!c.title?.trim() || !c.description?.trim()) {
        notify.error(`Commitment #${i + 1} needs both a title and description`);
        return;
      }
    }
    setSavingCommitments(true);
    try {
      await axios.put(`${API}/org/commitments`, {
        commitments: commitments.map(c => ({ title: c.title.trim(), description: c.description.trim() })),
      });
      notify.success('Commitments saved');
    } catch (err) {
      notify.error(err?.response?.data?.detail || 'Failed to save commitments');
    } finally {
      setSavingCommitments(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div style={{ minHeight: '100vh', background: THEME.surface, paddingBottom: 40 }} data-testid="edit-org-page">
      <div style={{ padding: '18px 20px', background: THEME.white, borderBottom: `1px solid ${THEME.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button data-testid="edit-org-back-btn" onClick={() => navigate('/organization')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
          <ArrowLeft size={20} color={THEME.text} />
        </button>
        <div style={{ fontSize: 16, fontWeight: 600, color: THEME.text }}>Edit organization page</div>
      </div>

      <div style={{ padding: '20px', maxWidth: 600, margin: '0 auto' }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: THEME.textMuted, fontSize: 13 }}>Loading…</div>
        ) : (
          <>
            {/* Targets */}
            <div style={{ marginBottom: 32 }}>
              <div style={sectionTitle}>
                <Target size={16} color={THEME.primary} /> Organization Targets
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {targets.length === 0 && (
                  <div style={{ padding: 16, background: THEME.white, borderRadius: 10, border: `1px dashed ${THEME.border}`, textAlign: 'center', fontSize: 13, color: THEME.textSecondary }}>
                    No targets yet. Add your first below.
                  </div>
                )}
                {targets.map((t, idx) => (
                  <div key={`${t.metric_id}-${idx}`} data-testid={`target-row-${idx}`} style={{ padding: 14, background: THEME.white, borderRadius: 10, border: `1px solid ${THEME.border}` }}>
                    <div style={{ marginBottom: 10 }}>
                      <label style={labelStyle}>Metric</label>
                      <select
                        data-testid={`target-metric-${idx}`}
                        value={t.metric_id}
                        onChange={(e) => updateTargetField(idx, 'metric_id', e.target.value)}
                        disabled={t._persisted}
                        style={inputStyle}
                      >
                        {/* If persisted, show its metric even if in "used" set. */}
                        {[t.metric_id, ...availableMetrics().map(m => m.metric_id).filter(id => id !== t.metric_id)].map(mid => {
                          const def = metricDefs.find(m => m.metric_id === mid);
                          return <option key={mid} value={mid}>{def ? `${def.label} (${def.unit})` : mid}</option>;
                        })}
                      </select>
                    </div>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>Target</label>
                        <input
                          data-testid={`target-value-${idx}`}
                          type="number" step="any" min="0.0001"
                          value={t.target}
                          onChange={(e) => updateTargetField(idx, 'target', e.target.value)}
                          style={inputStyle}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>Baseline</label>
                        <input
                          data-testid={`target-baseline-${idx}`}
                          type="number" step="any" min="0"
                          value={t.baseline}
                          onChange={(e) => updateTargetField(idx, 'baseline', e.target.value)}
                          style={inputStyle}
                        />
                      </div>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={labelStyle}>Baseline method (optional)</label>
                      <input
                        data-testid={`target-baseline-method-${idx}`}
                        value={t.baseline_method}
                        onChange={(e) => updateTargetField(idx, 'baseline_method', e.target.value)}
                        placeholder="e.g. FY24 baseline, self-reported"
                        style={inputStyle}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        data-testid={`target-remove-${idx}`}
                        onClick={() => removeTargetRow(idx)}
                        style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${THEME.border}`, background: THEME.white, color: THEME.danger, cursor: 'pointer', fontSize: 12, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <Trash2 size={13} /> Remove
                      </button>
                      <button
                        data-testid={`target-save-${idx}`}
                        onClick={() => saveTarget(idx)}
                        disabled={savingTargetId === t.metric_id}
                        style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: THEME.primary, color: THEME.white, cursor: savingTargetId === t.metric_id ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <Save size={13} /> {savingTargetId === t.metric_id ? 'Saving…' : (t._persisted ? 'Update' : 'Save')}
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  data-testid="add-target-btn"
                  onClick={addTargetRow}
                  disabled={availableMetrics().length === 0}
                  style={{ padding: '10px 12px', borderRadius: 10, border: `1px dashed ${THEME.border}`, background: THEME.white, color: THEME.primary, cursor: availableMetrics().length === 0 ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <Plus size={14} /> Add target
                </button>
              </div>
            </div>

            {/* Commitments */}
            <div>
              <div style={sectionTitle}>
                <Heart size={16} color={THEME.primary} /> Our Commitments <span style={{ fontSize: 11, fontWeight: 500, color: THEME.textMuted, textTransform: 'none', letterSpacing: 0 }}>({commitments.length}/{MAX_COMMITMENTS})</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {commitments.length === 0 && (
                  <div style={{ padding: 16, background: THEME.white, borderRadius: 10, border: `1px dashed ${THEME.border}`, textAlign: 'center', fontSize: 13, color: THEME.textSecondary }}>
                    No commitments yet. Add up to {MAX_COMMITMENTS}.
                  </div>
                )}
                {commitments.map((c, idx) => (
                  <div key={idx} data-testid={`commitment-row-${idx}`} style={{ padding: 14, background: THEME.white, borderRadius: 10, border: `1px solid ${THEME.border}` }}>
                    <div style={{ marginBottom: 10 }}>
                      <label style={labelStyle}>Title</label>
                      <input
                        data-testid={`commitment-title-${idx}`}
                        maxLength={80}
                        value={c.title}
                        onChange={(e) => updateCommitmentField(idx, 'title', e.target.value)}
                        placeholder="e.g. Ethical Business Practices"
                        style={inputStyle}
                      />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={labelStyle}>Description</label>
                      <textarea
                        data-testid={`commitment-description-${idx}`}
                        maxLength={280}
                        value={c.description}
                        onChange={(e) => updateCommitmentField(idx, 'description', e.target.value)}
                        rows={2}
                        placeholder="Short description of this commitment"
                        style={{ ...inputStyle, resize: 'vertical' }}
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        data-testid={`commitment-remove-${idx}`}
                        onClick={() => removeCommitment(idx)}
                        style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${THEME.border}`, background: THEME.white, color: THEME.danger, cursor: 'pointer', fontSize: 12, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <Trash2 size={13} /> Remove
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  data-testid="add-commitment-btn"
                  onClick={addCommitment}
                  disabled={commitments.length >= MAX_COMMITMENTS}
                  style={{ padding: '10px 12px', borderRadius: 10, border: `1px dashed ${THEME.border}`, background: THEME.white, color: THEME.primary, cursor: commitments.length >= MAX_COMMITMENTS ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <Plus size={14} /> Add commitment
                </button>
                <button
                  data-testid="save-commitments-btn"
                  onClick={saveCommitments}
                  disabled={savingCommitments}
                  style={{ padding: '12px 14px', borderRadius: 10, border: 'none', background: THEME.primary, color: THEME.white, cursor: savingCommitments ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 4 }}
                >
                  <Save size={14} /> {savingCommitments ? 'Saving…' : 'Save commitments'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
